import { SignupPayload } from '../../core/models/SignupPayload';
import { LoginPayload } from '../../core/models/LoginPayload';
import { environment } from '../../environment/environment';
import { axiosV1, axiosV2 } from '../../core/api/Axios';
import { AuthRepository } from './AuthRepository';
import { AuthData, AuthDataMapper, ExchangePayload, ServerAuthData } from './AuthData';
import { ApiResult } from '../../core/models/ApiResponse';
import { ServerUser, User } from './User';
import firebase from 'firebase';
import { asyncTryCatch, Either, right } from '../../core/functional/Either';

const AuthRepositoryImpl: AuthRepository = {
  async signup(payload: SignupPayload) {
    return asyncTryCatch(async () => {
      const body = {...payload, subscription_platform: 'custom'};
      const { data: authData } = await axiosV2.post<{ data: AuthData; }>(`${environment.API_BASE_URL_V2}auth/brand/register-and-complete`, body);
      return AuthDataMapper.map(authData);
    }, e => {
      if (e instanceof Error) {
        return e;
      } else {
        return new Error();
      }
    });
  },

  async login(payload: LoginPayload): Promise<ApiResult<AuthData>> {
    return asyncTryCatch(async () => {
      const { data } = await axiosV2.post<ServerAuthData>(`${environment.API_BASE_URL_V2}auth/login`, payload);
      return AuthDataMapper.map(data);
    }, e => {
      if (e instanceof Error) {
        return e;
      } else {
        return new Error();
      }
    });
  },

  /**
   * Trade the single-use handle from the URL for the auth payload.
   *
   * The URL used to carry the payload itself, base64-encoded - a live Firebase
   * custom token and the legacy token, readable by anyone who saw the URL, and
   * kept in browser history and the hosting logs besides.
   *
   * Deliberately axiosV1, not axiosV2: the V2 response interceptor shows a red
   * toast on every failure, and a failed exchange here is an ORDINARY outcome -
   * the handle is spent, or expired after its minute, or the user reloaded or
   * pressed back. AuthProvider already handles that by falling through to the
   * signed-out path; a toast on top of it would tell the user something broke
   * when nothing did.
   */
  async exchangeHandle(handle: string): Promise<Either<Optional<ExchangePayload>>> {
    return asyncTryCatch(async () => {
      const { data } = await axiosV1.post<{ data: ExchangePayload; }>(
        `${environment.API_BASE_URL_V2}auth/exchange`,
        { handle }
      );
      return data.data;
    }, e => (e instanceof Error ? e : new Error()));
  },

  /**
   * Ask for a fresh handle to hand the session to another app.
   *
   * Sends no body: the whole payload is built server side from the Firebase ID
   * token in the header, including the legacy Django token. We used to post our
   * own copy of that token, which put a permanent credential into a request body
   * the API logs in full.
   */
  async mintHandle(): Promise<Either<string>> {
    return asyncTryCatch(async () => {
      const idToken = await firebase.auth().currentUser?.getIdToken();
      const { data } = await axiosV2.post<{ data: { handle: string; }; }>(
        `${environment.API_BASE_URL_V2}auth/handle`,
        {},
        { headers: { ia_auth_token: idToken as string } }
      );
      return data.data.handle;
    }, e => (e instanceof Error ? e : new Error()));
  },

  async completeLogin(token) {
    return asyncTryCatch(async () => {
      await firebase.auth().signInWithCustomToken(token);
    }, e => {
      if (e instanceof Error) {
        return e;
      } else {
        return new Error();
      }
    });
  },

  async getUser(token): Promise<ApiResult<User>> {
    return asyncTryCatch(async () => {
      const { data } = await axiosV1.get<ServerUser>(`${environment.API_BASE_URL_V1}me/`, {
        headers: {
          'Authorization': `Token ${token}`
        }
      });
      return data;
    }, e => {
      if (e instanceof Error) {
        return e;
      } else {
        return new Error();
      }
    });
  },

  setApiAuthorization(token?: string) {
    if (token) {
      axiosV2.defaults.headers.common['ia_auth_token'] = `${token}`;
    } else {
      delete axiosV2.defaults.headers.common['ia_auth_token'];
    }
  },

  init(): void {
    firebase.initializeApp(environment.FIREBASE_CONFIG);
  },

  isLoggedIn(): boolean {
    return !!firebase.auth().currentUser;
  },

  listenUser(callback): () => void {
    return firebase.auth().onAuthStateChanged(user => callback(!!user));
  },

  async logout(): Promise<ApiResult<void>> {
    return asyncTryCatch(async () => {
      return firebase.auth().signOut();
    }, e => {
      if (e instanceof Error) {
        return e;
      } else {
        return new Error();
      }
    });
  },

  async updateLocalUser() {
    return asyncTryCatch(async () => {
      const token = await firebase.auth().currentUser?.getIdToken();
      AuthRepositoryImpl.setApiAuthorization(token);
    }, e => {
      if (e instanceof Error) {
        return e;
      } else {
        return new Error();
      }
    });
  },

  async getAuthToken(): Promise<ApiResult<Optional<string>>> {
    return right(await firebase.auth().currentUser?.getIdToken());
  }
};

export default AuthRepositoryImpl;
